<template>
  <div
    :id="$props.id"
    ref="dropdownRef"
    class="dropdown"
    :class="{ [`dropdown--${$props.size}`]: true }"
    :popover="$props.manualFocus ? 'manual' : 'auto'"
    :style="{ 'position-anchor': `--${$props.id}` }"
  >
    <div
      v-if="$props.multiSelect"
      class="dropdown__item--checkbox"
    >
      <basic-checkbox
        v-model="selectAllOption.checked"
        :name="`${props.name}_all`"
        :label="selectAllOption.label"
      />
    </div>

    <template
      v-for="(item, index) in ($props.multiSelect ? dropdownOptions : $props.options)"
      :key="index"
    >
      <div
        v-if="$props.multiSelect"
        class="dropdown__item--checkbox"
      >
        <basic-checkbox
          v-model="dropdownOptions[index].checked"
          :name="`${props.name}_${item.label}`"
          :label="item.label"
        />
      </div>

      <template v-else>
        <input
          :id="`${$props.name}-${item.value}`"
          type="radio"
          :name="$props.name"
          :value="item.value"
          :checked="item.value === $props.selected"
          :disabled="item.disabled"
          @keydown.tab="selectItem(item)"
          @keydown.enter.prevent="selectItem(item)"
        >
        <label
          class="dropdown__item"
          :for="`${$props.name}-${item.value}`"
          @click="selectItem(item)"
        >
          <template v-if="item.component">
            <component
              :is="{...item.component}"
              v-if="item.component"
              :code="item.code"
            />
          </template>

          <template v-else>
            <span v-html="item.label" />
          </template>

          <the-icon
            v-if="item.value === $props.selected"
            art="solid"
            name="check"
          />
        </label>
      </template>
    </template>
  </div>
</template>

<script setup>
  /**
   * FIGMA: https://www.figma.com/file/3r1w8rChrH97KwxTcUblDL/EP-Basic-Library?node-id=244%3A6421&mode=dev
   */
  import { onMounted, ref, useTemplateRef, watch } from 'vue'

  import useI18n from '@shared/hooks/useI18n'

  import { DEFAULT_FOCUS_DELAY } from '@shared/config/constants'

  import BasicCheckbox from '@shared/components/Basic/Checkbox'

  // HOOKS
  const { t } = useI18n()

  // INIT
  const emit = defineEmits(['close', 'select'])
  defineOptions({
    inheritAttrs: false,
  })
  const props = defineProps({
    id: {
      type: String,
      required: true
    },

    manualFocus: {
      type: Boolean,
      default: false
    },

    multiSelect: {
      type: Boolean,
      default: false,
    },

    name: {
      type: String,
      required: true
    },

    options: {
      type: Array,
      required: true
    },

    selected: {
      type: [String, Object, Number, Array],
      default: ''
    },

    size: {
      validator(value) {
        return ['sm', 'md'].includes(value)
      },
      default: 'sm',
    },
  })

  // DATA
  const dropdownOptions = ref(props.options)
  const elementRef = useTemplateRef('dropdownRef')
  const resetAllOptions = ref(true)
  const selectAllOption = ref({})

  // METHODS
  function close() {
    elementRef.value?.hidePopover()
    emit('close')
  }

  function handleClickOutside(event) {
    if (event && !event.pointerType) return

    if (!event.target.closest(`#dropdown-${props.id}`)) {
      close()
    }
  }

  function handleToggle(event) {
    if (event.newState === 'open') {

      // @note: is used for e.g. location-search, as on open, we can't move focus to the children
      if (!props.manualFocus) {
        window.setTimeout(() => {
          setFocus()
        })
      } else {
        window.setTimeout(() => {
          document.addEventListener('click', handleClickOutside)
        }, DEFAULT_FOCUS_DELAY)
      }
    } else {
      document.removeEventListener('click', handleClickOutside)
    }
  }

  function isOpen() {
    return elementRef.value.matches(':popover-open')
  }

  function open() {
    if (!isOpen()) {
      elementRef.value.showPopover()
    }
  }

  function selectItem(item) {
    emit('select', item)
  }

  function setFocus() {
    const hasDefault = elementRef.value.querySelector(':checked')

    if (hasDefault) {
      hasDefault.focus()
    } else if (elementRef.value.querySelector('.dropdown__item')) {
      elementRef.value.querySelector('.dropdown__item').focus()
    } else if (elementRef.value.querySelector('.dropdown__item--checkbox')) {
      elementRef.value.querySelector('.dropdown__item--checkbox').focus()
    }
  }

  function toggle() {
    elementRef.value.togglePopover()
  }

  // WATCHER
  watch(
    () => selectAllOption.value.checked,
    (newVal, oldVal) => {
      if (props.multiSelect && newVal !== oldVal) {
        if (!resetAllOptions.value) {
          resetAllOptions.value = true
          return
        }
        dropdownOptions.value = dropdownOptions.value.map(option => {
          return {
            ...option,
            checked: newVal,
          }
        })
      }
    }
  )

  watch(
    () => dropdownOptions.value,
    (val) => {
      if (props.multiSelect) {
        emit('select', dropdownOptions.value
          .filter(option => option.checked)
          .map(option => option.value)
        )

        const hasUncheckedOptions = val.some(v => !v.checked)
        if (hasUncheckedOptions && selectAllOption.value.checked) {
          resetAllOptions.value = false
          selectAllOption.value.checked = false
        }

        if (!hasUncheckedOptions && !selectAllOption.value.checked) {
          resetAllOptions.value = false
          selectAllOption.value.checked = true
        }
      }
    },
    { deep: true }
  )

  // LIFECYCLE HOOKS
  onMounted(() => {
    elementRef.value.addEventListener('beforetoggle', handleToggle)
    if (props.multiSelect) {
      dropdownOptions.value = dropdownOptions.value.map(option => {
        return {
          ...option,
          checked: props.selected.includes(option.value)
        }
      })

      selectAllOption.value = {
        label: t('form.multiSelect.selectAll'),
        checked: props.options.every(option => props.selected.includes(option)),
      }
    }
  })

  // EPILOGUE
  defineExpose({
    close,
    elementRef,
    isOpen,
    open,
    setFocus,
    toggle
  })
</script>

<style scoped>
  .dropdown--sm {
    --select-font-size: var(--font-size-regular-md);
    --select-letter-spacing: var(--letter-spacing-regular-md);
    --select-line-height: var(--line-height-regular-md);
  }

  .dropdown--md {
    --select-font-size: var(--font-size-regular-xl);
    --select-letter-spacing: var(--letter-spacing-regular-xl);
    --select-line-height: var(--line-height-regular-xl);
  }

  .dropdown {
    --select-color-item: var(--on-surface);
    --select-background-color-item: var(--Interaction-States-Dropdowns-default);
    --select-background-color-item-hovered: var(--Interaction-States-Dropdowns-hovered);
    --select-background-color-item-focused: var(--Interaction-States-Dropdowns-focused);
    --select-background-color-item-active: var(--Interaction-States-Dropdowns-pressed);
    --select-background-color-item-selected: var(--Interaction-States-Dropdowns-Selected);
    --select-background-color-item-disabled: var(--Interaction-States-Dropdowns-disabled);

    position: fixed;
    inset: unset;
    width: anchor-size(width);
    border: none;
    height: fit-content;
    max-height: 350px;
    background-color: var(--on-primary);
    border-radius: var(--fixed-border-radius-fix-02);
    box-shadow: var(--elevation-level-3);
    padding: 0;
    opacity: 0;
    transform: scale(0.95) translateY(4px);
    transition: opacity 0.15s, transform 0.15s, display 0.15s allow-discrete;

    &:popover-open {
      opacity: 1;
      transform: scale(1) translateY(0);
    }

    position-area: bottom;
    position-try-fallbacks: top, right, left;

    .dropdown__item--checkbox {
      cursor: pointer;
      padding: var(--fixed-spacing-fix-02) var(--fixed-spacing-fix-04);
    }

    .dropdown__item--checkbox:first-child {
      padding: var(--fixed-spacing-fix-03) var(--fixed-spacing-fix-04) var(--fixed-spacing-fix-02);
      border-bottom: 1px solid var(--outline-variant);
    }

    .dropdown__item--checkbox:last-child {
      padding: var(--fixed-spacing-fix-02) var(--fixed-spacing-fix-04) var(--fixed-spacing-fix-03);
    }

    .dropdown__item--checkbox:has(.checkbox--selected) {
      background-color: var(--select-background-color-item-selected);
      color: var(--secondary);
    }

    .dropdown__item--checkbox:hover {
      background-color: var(--surface-warm);
    }

    .dropdown__item--checkbox:focus {
      background-color: var(--tertiary-container-low);
    }

    .dropdown__item {
      align-items: center;
      cursor: pointer;
      display: flex;
      gap: var(--fixed-spacing-fix-04);
      justify-content: space-between;
      width: 100%;
      color: var(--select-color-item);
      word-break: break-word;
      font-size: var(--select-font-size);
      letter-spacing: var(--select-letter-spacing);
      line-height: var(--select-line-height);
      padding: var(--fixed-spacing-fix-02) var(--fixed-spacing-fix-06) var(--fixed-spacing-fix-02) var(--fixed-spacing-fix-04);

      &:hover {
        background-color: var(--select-background-color-item-hovered) !important;
      }
    }

    input[type='radio'] {
      position: absolute;
      left: 0;
      opacity: 0;

      &:disabled + label{
        pointer-events: none;
        text-decoration: line-through;
        background-color: var(--select-background-color-item-disabled);
      }

      &:focus + label{
        background-color: var(--select-background-color-item-focused);
      }

      &:checked + label {
        background-color: var(--select-background-color-item-selected);

        svg {
          width: 16px;
          height: 16px;
          color: var(--on-surface-medium);
        }
      }
    }
  }


  @starting-style {
    .dropdown:popover-open {
      opacity: 0;
      transform: scale(0.95) translateY(4px);
    }
  }
</style>
